/**
 * The seller-chosen "Suggested products" row under a product page.
 *
 * WHY THIS IS A LIB. The stored shape is written by TWO editors that share no
 * code (the create form and the edit form), read by the public product page,
 * and validated a fourth time by the finalize dialog that decides whether the
 * product may be published at all. Those four have to agree on what the field
 * is, what an empty selection means, and what happens to an id pointing at a
 * product that is no longer live, or a seller publishes a page with an empty
 * row on it and nothing tells them.
 *
 * WHY IT LIVES ON `details`. Same reasoning as the gallery settings beside it
 * (see src/lib/product-gallery.ts): `product.design` is gated behind the
 * "Customise this product page" master switch and holds theme overrides. Which
 * of your own products you cross-sell is content, not a theme, and a seller
 * should not have to turn on a dark-mode background effect to set it.
 */

/**
 * Most products a seller may put in the row.
 *
 * Four, because the row is one line of cards on a desktop grid and a fifth
 * would wrap into a second row that reads as a second section. It is also a
 * curation limit rather than a technical one: a seller who picks four has
 * chosen; a seller who could pick twenty would paste their whole catalogue in
 * and the row would stop meaning anything.
 */
export const MAX_SUGGESTED_PRODUCTS = 4;

export type SuggestedProducts = {
  /**
   * OFF by default, for new products and for every product written before this
   * field existed. A row of the seller's other products is a deliberate
   * cross-sell, not something that should appear under someone's page because
   * a default flipped.
   */
  enabled: boolean;
  /** In the order the seller arranged them. */
  productIds: string[];
};

export const EMPTY_SUGGESTED_PRODUCTS: SuggestedProducts = { enabled: false, productIds: [] };

/**
 * Read a stored value defensively: `details` is client-written and, for a live
 * product, world-readable.
 */
export function readSuggestedProducts(details: unknown): SuggestedProducts {
  if (!details || typeof details !== 'object' || Array.isArray(details)) return EMPTY_SUGGESTED_PRODUCTS;
  const raw = (details as { suggestedProducts?: unknown }).suggestedProducts;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return EMPTY_SUGGESTED_PRODUCTS;
  const row = raw as { enabled?: unknown; productIds?: unknown };
  const ids = Array.isArray(row.productIds)
    ? row.productIds.filter((v): v is string => typeof v === 'string' && v.length > 0)
    : [];
  return {
    enabled: row.enabled === true,
    // Deduped and clipped on the way IN as well as on the way out. The cap is
    // what a visitor renders even if a larger list is sitting in the document
    // right now, and duplicates would put the same card in the row twice.
    productIds: Array.from(new Set(ids)).slice(0, MAX_SUGGESTED_PRODUCTS),
  };
}

/**
 * Turn a seller's live products plus this product's stored selection into the
 * cards that actually render.
 *
 * The stored ids are a WISH, not a source of truth: `sellerProducts` is. Pass
 * it the seller's own live products (what `useSellerProducts` returns, which is
 * already scoped to `sellerId == X, status == 'live'`) and an id naming a
 * product that has been archived, unlisted, soft-deleted or that belongs to
 * somebody else resolves to nothing and disappears. That is what stops the row
 * rendering a hole, and what stops one seller pulling a competitor's product
 * under their page by pasting its id.
 *
 * The product's OWN id is dropped whatever the stored list says. A page
 * suggesting itself is a link back to where the buyer already is, and the edit
 * form cannot be relied on to have excluded it: the id is unknown while a
 * product is still being created, and a seller could have picked a product that
 * was later merged into this one.
 *
 * Returns an empty list when the switch is off, so a seller who turns the
 * section off keeps their selection in the document, waiting for them if they
 * turn it back on.
 *
 * @param sellerProducts The seller's live products, in any order.
 * @param details The stored `products/{id}.details`, or anything at all.
 * @param currentProductId The product whose page this is.
 */
export function resolveSuggestedProducts<P extends { id: string }>(
  sellerProducts: readonly P[],
  details: unknown,
  currentProductId: string,
): P[] {
  const { enabled, productIds } = readSuggestedProducts(details);
  if (!enabled || productIds.length === 0) return [];

  const byId = new Map<string, P>();
  for (const product of sellerProducts) {
    if (product?.id) byId.set(product.id, product);
  }

  const out: P[] = [];
  for (const id of productIds) {
    if (id === currentProductId) continue;
    const product = byId.get(id);
    if (product) out.push(product);
  }
  return out.slice(0, MAX_SUGGESTED_PRODUCTS);
}

/**
 * Whether the form may be finished.
 *
 * The rule the seller was given: the section is either off, or on with at least
 * one product in it. Enabled-with-nothing-selected is the one state that must
 * not reach the document, because it publishes a heading over an empty grid.
 *
 * This is BOTH forms' publish gate and it is stated once here rather than twice
 * in two 3000-line page components, which is how the create form and the edit
 * form would come to disagree about whether an empty row blocks publishing.
 */
export function suggestedProductsValid(enabled: boolean, productIds: readonly string[]): boolean {
  if (!enabled) return true;
  return productIds.filter(Boolean).length > 0;
}

/**
 * Clean an editor's working selection into what should be stored.
 *
 * Applied at SAVE time rather than continuously, so a seller can untick
 * everything while rearranging without the form deleting their section
 * underneath them mid-edit.
 *
 * Ids naming products that are no longer live are dropped here as well as in
 * the resolver. The resolver already ignores them, but leaving them in the
 * document means a row quietly regrows if a product is restored months later
 * into a page whose seller has long since stopped thinking about it.
 *
 * `null` FOR `liveProductIds` MEANS "LIVENESS UNKNOWN", AND IT IS NOT THE SAME
 * AS AN EMPTY LIST. Both forms source that list from a `useSellerProducts`
 * snapshot, which is empty in two situations a save can land in: before the
 * listener has reported, and after its error handler ran (it logs and gives up
 * with an empty map). Treating either as "the seller has no live products"
 * would drop every id and write back `enabled: true` with nothing in it, which
 * erases a selection the seller never touched, takes the row off their live
 * page, and then blocks their next publish on the empty-selection rule. So the
 * caller passes the ids only once it actually knows them, and passes `null`
 * otherwise: the dedupe and the cap still apply, and the liveness filter, the
 * one part that needs an answer we do not have, is skipped. The resolver is
 * still the backstop, so a stale id that survives here renders nothing.
 *
 * @param enabled The section switch.
 * @param productIds The editor's working selection, in the seller's order.
 * @param liveProductIds Every product id the seller currently has live, or
 *   `null` when that query has not reported yet.
 */
export function normalizeSuggestedProducts(
  enabled: boolean,
  productIds: readonly string[],
  liveProductIds: readonly string[] | null,
): SuggestedProducts {
  const live = liveProductIds === null ? null : new Set(liveProductIds);
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of productIds) {
    if (!id || seen.has(id)) continue;
    if (live && !live.has(id)) continue;
    seen.add(id);
    out.push(id);
    if (out.length === MAX_SUGGESTED_PRODUCTS) break;
  }
  return { enabled, productIds: out };
}
